import { useEffect, useState } from "react"

import { App, Col, Divider, Flex, Row, Space, Typography } from "antd"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { useSearchParams } from "react-router-dom"

import InputComponent from "../../Components/Input"
import Private from "../../Components/Private"
import Icon from "../../Icon"
import { validateUrl } from "../../Utils/utils"
import { authErrorReset } from "../LoginPage/redux/auth.actions"
import { userActions } from "../User/redux/user.slice"
import {
  getBusinessAccountsWithRole,
  getBusinessInfo,
  updateBusinessAccount
} from "../User/redux/user.thunks"
import TitleSection from "./components/TitleSection"

const { Title, Text } = Typography

const MyBusiness = ({
  saveFetching,
  isAdmin,
  userInfo,
  businessAccount,
  businessInfo,
  saveResult,
  nav,
  error,
  userError,
  ...props
}) => {
  const { name, url } = businessAccount || {
    name: null,
    url: null
  }

  const { t } = useTranslation()
  const { notification } = App.useApp()
  const [searchParams] = useSearchParams()

  const [data, setData] = useState({ name, url })

  const [field, setField] = useState("")

  const [urlError, setUrlError] = useState(false)
  const [visible, setVisible] = useState(null) // rdr or other or auto_cancel_amount

  const businessAccountId = isAdmin
    ? searchParams.get("business_account_id")
    : userInfo?.current_business_account_id

  const changedField = {
    name: t("Business account name"),
    url: t("Business URL")
  }

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content" }
  }

  const notificationSuccessText = (field) => {
    return {
      message: t("_field_ was replaced successfully", {
        field: changedField[field]
      })
    }
  }

  useEffect(() => {
    if (!businessAccountId) return
    props.getBusinessInfo({ business_account_id: businessAccountId })
  }, [businessAccountId])

  useEffect(() => {
    if (businessAccount?.id) setData({ name, url })
  }, [name, url, businessAccount?.id])

  useEffect(() => {
    if (error?.message) notification.error({ message: error?.message?.detail })
    if (error) props.resetError()
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [error])

  useEffect(() => {
    if (saveResult && !saveResult?.message) {
      if (field) {
        const message = notificationSuccessText(field)
        notification.success({
          ...message,
          ...notificationConfig
        })
        setField("")
        if (visible) setVisible(null)
        props.userSaveResultReset()
      }
    }
    if (userError) {
      if (userError?.confirmError?.detail) {
        notification.error({
          message: userError?.confirmError?.detail,
          ...notificationConfig
        })
        props.userErrorsReset()
      } else if (userError?.businessAccountError) {
        notification.error({
          message: userError?.businessAccountError || t("Something went wrong, kindly try again"),
          ...notificationConfig
        })
        props.userErrorsReset()
      }
    }
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [saveResult, userError, field])

  const handleUpdateUserInfo = (params, value, type) => {
    if (businessAccount[params] !== data[params]) {
      let newValue = data[params]?.trim()
      if (newValue) {
        if (params === "url" && !validateUrl.test(newValue)) {
          setUrlError(true)
          return
        }
        props.updateBusinessAccount({
          id: businessAccount.id,
          [params]: newValue
        })
      } else {
        let oldValue = null
        if (params === "name") oldValue = name
        if (params === "url") oldValue = url
        if (oldValue) setData((prev) => ({ ...prev, [params]: oldValue }))
      }
    } else {
      return
    }

    changePersonalData(params)
  }

  function changePersonalData(params) {
    setField(params)
  }

  function handleChange(e) {
    const { name, value } = e.target
    setData((prev) => ({ ...prev, [name]: value }))
  }

  return (
    <div style={{ padding: 32 }}>
      <TitleSection
        title={
          <>
            {"Business information for "}
            <Private>{businessAccount?.name}</Private>
          </>
        }
        link={businessAccount?.url}
      />

      <Row>
        <Col flex={"1 1 300px"}>
          <Space size={8} direction={"vertical"}>
            <Text strong>{t("Business name")}</Text>
            <Text type="secondary">
              {t("Change the business name. This field can not be blank.")}
            </Text>
          </Space>
        </Col>

        <Col flex={"0 1 420px"}>
          {businessAccount?.id && (
            <Flex gap={12} align={"center"} style={{ height: "100%" }}>
              <InputComponent
                name="name"
                defaultValue={name || ""}
                value={data.name}
                onBlur={() => handleUpdateUserInfo("name")}
                onChange={handleChange}
              />
            </Flex>
          )}
        </Col>
      </Row>

      <Divider />

      <Row>
        <Col flex={"1 1 300px"}>
          <Space size={8} direction={"vertical"}>
            <Text strong>{t("Business URL")}</Text>
            <Text type="secondary">
              {t("Change the business URL. This field can not be blank.")}
            </Text>
          </Space>
        </Col>

        <Col flex={"0 1 420px"}>
          {businessAccount?.id && (
            <Flex gap={4} align={"start"} justify={"center"} vertical style={{ height: "100%" }}>
              <InputComponent
                name="url"
                defaultValue={url || ""}
                value={data.url}
                onFocus={() => {
                  if (urlError) setUrlError(false)
                }}
                status={urlError ? "error" : null}
                onBlur={() => handleUpdateUserInfo("url")}
                onChange={handleChange}
              />

              {urlError && (
                <Space>
                  <Icon type={"alert_triangle_error"} />
                  <Text type="danger">{t("Please enter valid business URL.")}</Text>
                </Space>
              )}
            </Flex>
          )}
        </Col>
      </Row>

      <Divider />

      <Row>
        <Col flex={"1 1 300px"}>
          <Space size={8} direction={"vertical"}>
            <Text strong>{t("First and last name of the owner")}</Text>
            <Text type="secondary">{t("The name is non-editable.")}</Text>
          </Space>
        </Col>

        <Col flex={"0 1 420px"}>
          <Flex gap={12} align="center">
            <InputComponent disabled value={businessInfo?.first_name || "name"} />
            <InputComponent disabled value={businessInfo?.last_name || "surname"} />
          </Flex>
        </Col>
      </Row>

      <Divider />

      <Row>
        <Col flex={"1 1 300px"}>
          <Space size={8} direction={"vertical"}>
            <Text strong>{"Email address of the owner"}</Text>
            <Text type="secondary">{"The email address is non-editable."}</Text>
          </Space>
        </Col>

        <Col flex={"0 1 420px"}>
          <InputComponent disabled value={businessInfo?.owner_email || "test@gmail.com"} />
        </Col>
      </Row>
    </div>
  )
}

MyBusiness.propTypes = {
  fetching: PropTypes.bool,
  businessAccount: PropTypes.object
}

const mapStateToProps = (state) => ({
  nav: state.nav,
  isAdmin: state.auth.isAdmin,
  userInfo: state.auth.userInfo,

  //businessAccountFetching: state.user.businessAccountFetching,
  businessAccount: state.user.businessAccount,
  businessInfo: state.user.businessInfo,

  //businessAccounts: state.user.businessAccounts,

  saveFetching: state.user.saveFetching,

  saveResult: state.user.saveResult,
  error: state.auth.error,
  userError: state.user.errors
})

const mapDispatchToProps = (dispatch) => ({
  updateBusinessAccount: (data) => dispatch(updateBusinessAccount(data)),
  userSaveResultReset: () => dispatch(userActions.userSaveResultReset()),
  getBusinessAccountsWithRole: () => dispatch(getBusinessAccountsWithRole()),
  getBusinessInfo: (data) => dispatch(getBusinessInfo(data)),
  resetError: () => dispatch(authErrorReset()),
  userErrorsReset: () => dispatch(userActions.userErrorsReset())
})

export default connect(mapStateToProps, mapDispatchToProps)(MyBusiness)
